import cn from 'classnames';
import { noop } from 'lodash-es';
import { ComponentProps, FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/shared/ui';

import {
  isBlockNoteTemplate,
  type DocumentTemplate,
} from '@/entities/templates';

import {
  useDocumentTemplate,
  useNoteLists,
  useNoteTeethGroupName,
} from '../hooks';
import {
  buildPresetNoteSurface,
  isFieldListTemplate,
  type DraftAnswers,
} from '../lib';

import { DocumentRow } from './DocumentRow';
import { FieldInstruction } from './FieldInstruction';
import { FieldListTemplate } from './FieldListTemplate';
import styles from './TemplatePreview.module.scss';
import { TemplateLineSection, TemplateSection } from './TemplateSection';

const TemplateLoadState: FC<{
  failed: boolean;
  className?: string;
  onRetry: () => void;
}> = ({ failed, className, onRetry }) =>
  failed ? (
    <div className={cn(styles.state, className)}>
      <Button variant="secondary" size="medium" onClick={onRetry}>
        <FormattedMessage
          id="workspace.retryTemplate"
          defaultMessage="Template could not be loaded. Retry"
        />
      </Button>
    </div>
  ) : (
    <p className={cn('p3', styles.state, className)}>
      <FormattedMessage id="global.loading" defaultMessage="Loading…" />
    </p>
  );

// Loads a version into the store and shows its loading or retry state; the
// caller renders the version from the store once it is there.
export const TemplateLoader: FC<{ templateId: string }> = ({ templateId }) => {
  const { failed, retry } = useDocumentTemplate(templateId);
  return <TemplateLoadState failed={failed} onRetry={retry} />;
};

// How a summary or an evaluation is shown: editable where the edit is given.
type FieldListPreview = Omit<
  ComponentProps<typeof FieldListTemplate>,
  'template'
>;

type PreviewProps = {
  template: DocumentTemplate;
  fieldList?: FieldListPreview;
  className?: string;
};

export const TemplatePreview: FC<
  Omit<PreviewProps, 'template'> & { templateId: string }
> = ({ templateId, fieldList, className }) => {
  const { template, failed, retry } = useDocumentTemplate(templateId);
  return template ? (
    <TemplatePreviewContent
      template={template}
      fieldList={fieldList}
      className={className}
    />
  ) : (
    <TemplateLoadState failed={failed} className={className} onRetry={retry} />
  );
};

const NO_ANSWERS: DraftAnswers = { answers: {}, aiAnswers: {} };

// A block template read before any note exists: its lines as the note lays
// them out, with values to be chosen and linked lines dimmed.
const BlockTemplatePreview: FC<{ template: DocumentTemplate }> = ({
  template,
}) => {
  const { lists } = useNoteLists(template);
  const teethGroupName = useNoteTeethGroupName(template.language);
  const surface = buildPresetNoteSurface({
    template,
    presets: {},
    teethGroupName,
  });
  return (
    <fieldset className={styles.readOnly} disabled>
      {surface.sections.map((section, index) => (
        <TemplateLineSection
          key={section.id}
          section={template.sections[index]}
          surface={section}
          lists={lists}
        />
      ))}
    </fieldset>
  );
};

// Documents without a working copy (consultation documents, letters and
// reviews) are previewed as the template defines them: option fields stay
// read-only and sections without options are filled from the recording. A
// list of fields is read, or edited, as its own document.
export const TemplatePreviewContent: FC<PreviewProps> = ({
  template,
  fieldList,
  className,
}) => (
  <section
    data-component="workspace-template-preview"
    className={cn(styles.preview, className)}
  >
    {isFieldListTemplate(template) ? (
      <FieldListTemplate {...fieldList} template={template} />
    ) : isBlockNoteTemplate(template) ? (
      <BlockTemplatePreview template={template} />
    ) : (
      template.sections.map((section) =>
        section.Options.length ? (
          <fieldset key={section.Code} className={styles.readOnly} disabled>
            <TemplateSection
              section={section}
              answers={NO_ANSWERS}
              multiSelect={{}}
              showInstructions
              onAnswer={noop}
            />
          </fieldset>
        ) : (
          <DocumentRow key={section.Code} label={section.Title}>
            <FieldInstruction instruction={section.Instructions ?? ''} />
          </DocumentRow>
        ),
      )
    )}
  </section>
);
